import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { lastValueFrom, RmsPulldownService, RmsMessageService, PromptService, ReportService, PopupService } from '@services';
import { RmsModule } from '@rms';
import { Column, HeaderButton, TableIcon } from '@interfaces';
import { take } from 'rxjs/operators';
import {
  PaymentsDataService, OrderPaymentStatusRow, OrderPaymentStatusTotals, PaymentOptions, ReceiptQueueSummary
} from '../../services/payments-data.service';
import { OrderPaymentsPopupComponent } from './order-payments-popup/order-payments-popup.component';
import { PartialPaymentComponent } from './partial-payment/partial-payment.component';
import { ReceiptQueueComponent } from './receipt-queue/receipt-queue.component';
import { NeedsReceiptComponent } from './needs-receipt/needs-receipt.component';
import { BatchProcessComponent } from './batch-process/batch-process.component';

// =====================================================================
// CustomerPaymentsComponent — Scope 7.2 Payment Management
// Accounting → Customer Payments
// ---------------------------------------------------------------------
// Tabs:
//   Order Payment Status  one row per order (legacy screen, below)          — this component
//   Receipts              one row per receipt, stages + filters             — ReceiptQueueComponent
//   Exceptions            stale / flagged receipts                          — ReceiptQueueComponent
//   Needs Receipt         5.4 payments with no receipt yet                  — NeedsReceiptComponent
// Every receipt opens the same pulldown (ReceiptDetailComponent).
// ---------------------------------------------------------------------
// Order Payment Status ("Order Payment Status" legacy screen, coffee)
// ---------------------------------------------------------------------
// One row per order, like dev-RMS Accounting → Order Payment Status:
//   Due Date (red when late) | Processed (click = remove processing date) | Paid | Posted |
//   Days Late | Cust # | Customer | D/O # (click = delivery order PDF) | Terms | Sale Date |
//   Amount (click = payments popup) | Balance | Bank ▾ | Pay Method ▾ | [Process / Post] |
//   % (partial payment) | Charged
// Process / Post use the Bank + Pay Method picked on the row.
// =====================================================================

/** Grid row = API row + the few display fields this screen adds */
interface GridRow extends OrderPaymentStatusRow {
  CustNo: string;
  ActionLabel: string;     // 'Process' | 'Post' | ''
}

interface StatCard {
  key: 'ALL' | 'OVERDUE' | 'PROCESS' | 'POST';
  label: string;
  cssClass: string;
}

@Component({
  selector: 'app-customer-payments',
  imports: [RmsModule, ReceiptQueueComponent, NeedsReceiptComponent],
  templateUrl: './customer-payments.component.html',
  styleUrl: './customer-payments.component.scss'
})
export class CustomerPaymentsComponent implements OnInit {
  // rms-table needs its columns before it renders and has no ngOnChanges on
  // columns — so the table sits behind this flag (known rms-table quirk).
  Loaded = false;
  Loading = false;
  Busy = false;                       // a Process / Post / remove is running

  AllRows: GridRow[] = [];
  FilteredRows: GridRow[] = [];       // real array, not a getter (rms-table data setter)
  Totals: OrderPaymentStatusTotals | null = null;
  Columns: Column[] = [];
  Options: PaymentOptions | null = null;
  QueueSummary: ReceiptQueueSummary | null = null;   // tab badges

  // Filters
  UnpaidOnly = true;
  MonthsBack = 12;
  MonthsOptions = [3, 6, 12, 24];
  ActiveCard: StatCard['key'] = 'ALL';
  LoadedAll = true;                   // false if more rows exist than we loaded

  // The API pages at 500 rows; we load up to this many and let rms-table page on screen
  private readonly MaxRows = 3000;
  private readonly ApiPageSize = 500;

  StatCards: StatCard[] = [
    { key: 'ALL', label: 'Unpaid orders', cssClass: 'card-all' },
    { key: 'OVERDUE', label: 'Overdue', cssClass: 'card-overdue' },
    { key: 'PROCESS', label: 'To process', cssClass: 'card-process' },
    { key: 'POST', label: 'To post', cssClass: 'card-post' },
  ];

  Buttons: HeaderButton[] = [
    { label: 'Refresh', onAction: () => this.loadList() },
    // 7.2.30 — pull settled coffee transactions from Braintree, match, post (PayPal later)
    { label: 'Batch Process', onAction: () => this.openBatchProcess() },
  ];

  constructor(
    private paymentsData: PaymentsDataService,
    private pulldownService: RmsPulldownService,
    private prompt: PromptService,
    private reportService: ReportService,
    private popupService: PopupService,
    private msg: RmsMessageService,
    private cdr: ChangeDetectorRef
  ) { }

  async ngOnInit() {
    try {
      this.Options = await lastValueFrom(this.paymentsData.getOptions());
    } catch (err: any) {
      this.msg.error(err?.error?.error ?? 'Could not load banks and pay methods.');
    }
    this.Columns = this.createColumns();
    await this.loadList();              // also refreshes the tab badges
    this.Loaded = true;
    this.cdr.markForCheck();
  }

  // ---------------------------------------------------------------------
  // Tabs
  // ---------------------------------------------------------------------

  get ExceptionsTabLabel(): string {
    const n = this.QueueSummary?.ExceptionTabCount ?? 0;
    return n ? `Exceptions (${n.toLocaleString()})` : 'Exceptions';
  }

  get NeedsReceiptTabLabel(): string {
    const n = this.QueueSummary?.NeedsReceiptCount ?? 0;
    return n ? `Needs Receipt (${n.toLocaleString()})` : 'Needs Receipt';
  }

  /** Counts for the tab labels (3rd recordset of the receipt queue; one row is enough) */
  async loadQueueSummary() {
    try {
      const r = await lastValueFrom(this.paymentsData.getQueue({ Stage: 'PROCESSING', PageNumber: 1, PageSize: 1 }));
      this.QueueSummary = r?.Summary ?? null;
    } catch {
      this.QueueSummary = null;          // badges are optional — no message
    } finally {
      this.cdr.markForCheck();
    }
  }

  /** Coming back to the first tab after working on receipts → refresh the grid */
  async onTabSelected(label: string) {
    if (label === 'Order Payment Status') await this.loadList();
    else this.loadQueueSummary();
  }

  // ---------------------------------------------------------------------
  // Data
  // ---------------------------------------------------------------------

  async loadList() {
    this.Loading = true;
    try {
      const rows: OrderPaymentStatusRow[] = [];
      let totals: OrderPaymentStatusTotals | null = null;
      let page = 1;

      // pull the API's 500-row pages until we have everything (or hit MaxRows)
      while (true) {
        const result = await lastValueFrom(this.paymentsData.getOrderStatus({
          UnpaidOnly: this.UnpaidOnly,
          MonthsBack: this.MonthsBack,
          SortBy: 'OrderID',
          SortDesc: true,
          PageNumber: page,
          PageSize: this.ApiPageSize
        }));
        totals = result?.Totals ?? totals;
        rows.push(...(result?.Rows ?? []));
        const total = totals?.TotalRows ?? 0;
        if (!result?.Rows?.length || rows.length >= total || rows.length >= this.MaxRows) break;
        page++;
      }

      this.Totals = totals;
      this.LoadedAll = rows.length >= (totals?.TotalRows ?? 0);
      this.AllRows = rows.map(r => this.toGridRow(r));
      this.applyFilter();
      this.loadQueueSummary();          // tab badges (Process / Post change the receipt counts)
    } catch (err: any) {
      this.msg.error(err?.error?.error ?? 'Could not load order payments.');
    } finally {
      this.Loading = false;
      this.cdr.markForCheck();
    }
  }

  private toGridRow(r: OrderPaymentStatusRow): GridRow {
    return {
      ...r,
      CustNo: `C-${r.CompanyID}`,
      ActionLabel: r.NextStep === 'PROCESS' ? 'Process' : r.NextStep === 'POST' ? 'Post' : ''
    };
  }

  applyFilter() {
    const rows = this.AllRows;
    switch (this.ActiveCard) {
      case 'OVERDUE': this.FilteredRows = rows.filter(r => (r.DaysLate ?? 0) > 0); break;
      case 'PROCESS': this.FilteredRows = rows.filter(r => r.NextStep === 'PROCESS'); break;
      case 'POST': this.FilteredRows = rows.filter(r => r.NextStep === 'POST'); break;
      default: this.FilteredRows = [...rows];
    }
    this.cdr.markForCheck();
  }

  selectCard(card: StatCard) {
    this.ActiveCard = card.key;
    this.applyFilter();
  }

  cardValue(card: StatCard): string {
    const t = this.Totals;
    if (!t) return '0';
    switch (card.key) {
      case 'OVERDUE': return `${t.OverdueCount ?? 0}`;
      case 'PROCESS': return `${t.ToProcessCount ?? 0}`;
      case 'POST': return `${t.ToPostCount ?? 0}`;
      default: return `${t.TotalRows ?? 0}`;
    }
  }

  cardSub(card: StatCard): number | null {
    const t = this.Totals;
    if (!t) return null;
    if (card.key === 'ALL') return t.TotalBalanceDue ?? 0;
    if (card.key === 'OVERDUE') return t.OverdueAmount ?? 0;
    return null;
  }

  async toggleUnpaidOnly() {
    this.UnpaidOnly = !this.UnpaidOnly;
    await this.loadList();
  }

  async changeMonths(months: number) {
    this.MonthsBack = Number(months) || 12;
    await this.loadList();
  }

  // ---------------------------------------------------------------------
  // Table
  // ---------------------------------------------------------------------

  // Widths add up to 95 + 5 for the "Partial" (%) icon column = 100, so the whole grid fits
  // on screen without scrolling sideways (rms-table makes the table wider than 100% otherwise).
  createColumns(): Column[] {
    const bankOptions = (this.Options?.Banks ?? [])
      .filter(b => b.IsActive)
      .map(b => ({ label: b.BankName, value: b.BankID }));

    const methodOptions = (this.Options?.DetailTypes ?? [])
      .filter(d => d.IsActive && !d.IsWithdrawal && d.PostsToLedger)   // money received only — no store-credit / adjustment types
      .map(d => ({ label: d.DetailTypeName, value: d.PaymentDetailTypeID }));

    return [
      {
        key: 'DueDate', width: 6, label: 'Due Date', type: 'date', SearchType: 'date', hidden: false,
        cellClass: (row: GridRow) => (row.DaysLate ?? 0) > 0 ? 'cell-overdue' : ''
      },
      {
        key: 'ProcessedDate', width: 6, label: 'Processed', type: 'date', SearchType: 'date', hidden: false,
        clickable: true, onActionKey: 'removeProcessing', onAction: (row: GridRow) => this.confirmRemoveProcessing(row)
      },
      { key: 'DatePaid', width: 5, label: 'Paid', type: 'date', SearchType: 'date', hidden: false },
      { key: 'PostedDate', width: 5, label: 'Posted', type: 'date', SearchType: 'date', hidden: false },
      {
        key: 'DaysLate', width: 4, label: 'Days Late', type: 'number', hidden: false,
        cellClass: (row: GridRow) => (row.DaysLate ?? 0) > 0 ? 'cell-overdue' : ''
      },
      { key: 'CustNo', width: 5, label: 'Cust #', hidden: false },
      { key: 'CompanyName', width: 9, label: 'Customer', hidden: false },
      {
        key: 'OrderID', width: 5, label: 'D/O #', type: 'number', hidden: false,
        clickable: true, onActionKey: 'openDeliveryOrder', onAction: (row: GridRow) => { this.openDeliveryOrder(row); }
      },
      { key: 'TermsText', width: 6, label: 'Terms', type: 'select', SearchType: 'select', hidden: false },
      { key: 'SaleDate', width: 6, label: 'Sale Date', type: 'date', SearchType: 'date', hidden: false },
      {
        key: 'OrderTotal', width: 6, label: 'Amount', type: 'currency', align: 'right', format: '1.2-2', hidden: false,
        clickable: true, onActionKey: 'openPayments', onAction: (row: GridRow) => this.openPayments(row)
      },
      { key: 'BalanceDue', width: 6, label: 'Balance', type: 'currency', align: 'right', format: '1.2-2', hidden: false },
      { key: 'BankID', width: 8, label: 'Bank', type: 'select', edit: true, selectOptions: bankOptions, hidden: false },
      { key: 'PaymentDetailTypeID', width: 9, label: 'Pay Method', type: 'select', edit: true, selectOptions: methodOptions, hidden: false },
      {
        key: 'ActionLabel', width: 6, label: 'Action', hidden: false,
        clickable: true, onActionKey: 'runNextStep', onAction: (row: GridRow) => { this.runNextStep(row); },
        cellClass: (row: GridRow) => row.ActionLabel ? `pay-action pay-action-${row.ActionLabel.toLowerCase()}` : ''
      },
      { key: 'Charged', width: 3, label: 'Charged', type: 'checkbox', hidden: false },
      // extra detail, hidden by default (column picker)
      { key: 'InvoiceDate', width: 6, label: 'Invoiced', type: 'date', SearchType: 'date', hidden: true },
      { key: 'SettledPaid', width: 6, label: 'Paid Amount', type: 'currency', align: 'right', format: '1.2-2', hidden: true },
      { key: 'PendingPaid', width: 6, label: 'Pending', type: 'currency', align: 'right', format: '1.2-2', hidden: true },
      { key: 'PickupDate', width: 6, label: 'Pickup', type: 'date', SearchType: 'date', hidden: true },
    ];
  }

  /** "%" icon on every row that still owes money */
  getRowIcons = (row: GridRow): TableIcon[] =>
    row.NextStep === 'PAID'
      ? []
      : [{ icon: 'percent', title: 'Partial payment', action: () => this.openPartial(row) }];

  // ---------------------------------------------------------------------
  // Row actions
  // ---------------------------------------------------------------------

  /** Process / Post button */
  async runNextStep(row: GridRow) {
    if (this.Busy || !row.ActionLabel) return;
    if (!row.BankID) { this.msg.warn('Choose the bank first.'); return; }

    this.Busy = true;
    try {
      if (row.NextStep === 'PROCESS') {
        if (!row.PaymentDetailTypeID) { this.msg.warn('Choose the pay method first.'); return; }
        await lastValueFrom(this.paymentsData.processOrder({
          OrderID: row.OrderID,
          BankID: row.BankID,
          PaymentDetailTypeID: row.PaymentDetailTypeID
        }));
        this.msg.success(`Order ${row.OrderID} processed.`);
      } else if (row.NextStep === 'POST') {
        const posted = await lastValueFrom(this.paymentsData.postOrder(row.OrderID, PaymentsDataService.today(), row.BankID));
        this.msg.success(`Order ${row.OrderID} posted (${posted?.length ?? 0} payment${posted?.length === 1 ? '' : 's'}).`);
      }
      await this.loadList();
    } catch (err: any) {
      this.msg.error(err?.error?.error ?? 'The payment could not be saved.');
    } finally {
      this.Busy = false;
      this.cdr.markForCheck();
    }
  }

  /** Click on the Processed date — same question as the old screen */
  confirmRemoveProcessing(row: GridRow) {
    if (this.Busy || !row.ProcessedDate) return;
    const { closed$ } = this.prompt.open({
      promptText: `Are you sure you want to remove the processing date from order ${row.OrderID}?`,
      buttons: [{ label: 'Remove', buttonClass: 'btn-bg-red', value: true }]
    });
    closed$.pipe(take(1)).subscribe(async (ok: any) => {
      if (!ok) return;
      this.Busy = true;
      try {
        await lastValueFrom(this.paymentsData.unprocessOrder(row.OrderID));
        this.msg.success(`Processing date removed from order ${row.OrderID}.`);
        await this.loadList();
      } catch (err: any) {
        this.msg.error(err?.error?.error ?? 'Could not remove the processing date.');
      } finally {
        this.Busy = false;
        this.cdr.markForCheck();
      }
    });
  }

  /** Click on D/O # — the 5.2 delivery order PDF in the in-app viewer */
  async openDeliveryOrder(row: GridRow) {
    if (this.Busy) return;
    this.Busy = true;
    try {
      const dataUrl: string = await this.reportService.generatePDF('DeliveryOrder', { OrderID: row.OrderID });
      // blob URL instead of the data: URL so the browser's PDF viewer shows it inside the popup
      const blob = await (await fetch(dataUrl)).blob();
      this.popupService.openFileViewer({
        title: `Delivery Order — ${row.OrderID}`,
        fileUrl: URL.createObjectURL(blob)
      });
    } catch (err: any) {
      this.msg.error(err?.error?.error ?? err?.message ?? 'Could not open the delivery order.');
    } finally {
      this.Busy = false;
      this.cdr.markForCheck();
    }
  }

  /** Click on Amount — payments on this order, total paid, left to pay */
  openPayments(row: GridRow) {
    const ref = this.pulldownService.open(OrderPaymentsPopupComponent, {
      data: { OrderID: row.OrderID },
      title: `Order Payments — ${row.OrderID} · ${row.CompanyName}`,
      width: '640px',
      maxWidth: '95vw',
      center: true
    });
    ref.afterClosed().pipe(take(1)).subscribe((result: any) => {
      if (result?.openPartial) this.openPartial(row);
    });
  }

  /** Batch Process (7.2.30) — Braintree pull / match / post */
  openBatchProcess() {
    const ref = this.pulldownService.open(BatchProcessComponent, {
      data: { Options: this.Options },
      title: 'Batch Process — Braintree (coffee)',
      width: '1200px',
      maxWidth: '97vw',
      center: true
    });
    // refresh on every close — the pulldown's own "×" closes with no result even after posting
    ref.afterClosed().pipe(take(1)).subscribe(async () => await this.loadList());
  }

  /** "%" — partial payment (method, bank, amount → Post Payment) */
  openPartial(row: GridRow) {
    const ref = this.pulldownService.open(PartialPaymentComponent, {
      data: {
        OrderID: row.OrderID,
        CompanyName: row.CompanyName,
        LeftToProcess: row.LeftToProcess,
        BankID: row.BankID,
        PaymentDetailTypeID: row.PaymentDetailTypeID,
        Options: this.Options
      },
      title: `Partial Payment — ${row.OrderID} · ${row.CompanyName}`,
      width: '560px',
      maxWidth: '95vw',
      center: true
    });
    // Always refresh on close — the pulldown's own "×" closes with no result
    ref.afterClosed().pipe(take(1)).subscribe(async () => await this.loadList());
  }
}
